import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { MESSAGES } from "~/resources/Message";
import { useAppSelector } from "~/hooks/appSelector";
import { FormScript, FormScriptResponse } from "~/models/Comons";
import { useSaveScriptMutation } from "~/services/script";
import { selectProvider } from "~/store/feature/Provider";

export const UseFormScript = (
  editedItem: FormScriptResponse | null,
  onCloseModal: () => void,
  refetch: () => void
) => {
  const { cloudProviderName } = useAppSelector(selectProvider);
  const [saveScript, { isLoading }] = useSaveScriptMutation();

  const editedCloudProvider = cloudProviderName.find(
    (providerItem) => providerItem.id === editedItem?.scriptIdentify?.id
  );

  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<FormScript>({
    defaultValues: {
      title: editedItem?.title ?? "",
      description: editedItem?.description ?? "",
      location: editedItem?.location ?? "",
      cloudProvider: editedCloudProvider
        ? { label: editedCloudProvider?.cloudProviderName, value: editedCloudProvider?.id }
        : undefined,
      script: null,
      configFiles: null,
    },
  });

  const currentScript = watch("script");
  const currentConfigFiles = watch("configFiles");

  // Handler for script file drop
  const onScriptDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0] || null;

      setValue("script", file);
      if (file) {
        clearErrors("script");
      } else {
        setError("script", {
          type: "manual",
          message: MESSAGES.MessageUploadScript,
        });
      }
    },
    [setValue, clearErrors, setError]
  );

  // Handler for config files drop (not required, so no error if null)
  const onConfigDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0] || null;

      setValue("configFiles", file);
      if (file) {
        clearErrors("configFiles"); 
      }
      // No error set if file is null, as configFiles is optional
    },
    [setValue, clearErrors]
  );

 const submitForm = async (_data: FormScript) => {
  const data = { ..._data };

  // Prepare API payload separately to avoid mutating typed form data
  const payload:any = {
    ...data,
    cloudProvider: data.cloudProvider
      ? {
          cloudProviderName: data.cloudProvider.label,
          id: data.cloudProvider.value,
        }
      : undefined,
  };

  if (!data.script && !editedItem) {
    setError("script", {
      type: "manual",
      message: MESSAGES.MessageUploadScript,
    });
    return;
  }

  try {
    toast.promise(
      (async () => {
        const response = await saveScript(payload).unwrap();
        if (response.success) {
          refetch();
          onCloseModal();
        }
      })(),
      {
        pending: `${editedItem ? "Updating" : "Saving"} Script`,
        success: MESSAGES.MessageSucessFully,
        error: MESSAGES.MessageErrorSavingScript,
      }
    );
  } catch (error) {
    console.error(MESSAGES.MessageErrorSavingScript, error);
  }
};

  return {
    handleSubmit,
    control,
    errors,
    currentScript,
    currentConfigFiles,
    onScriptDrop,
    onConfigDrop,
    submitForm,
    isLoading,
    cloudProviderName,
  };
};